/**
 * Pure helpers for `@mention` autocomplete in text composers (post composer,
 * chat composer, etc.). DOM-free and framework-free so the logic can be unit
 * tested in isolation.
 *
 * Mirrors the iOS implementation in
 * `ZoeyApp/ZoeyApp/Views/Posts/AddEditPostView.swift:77-88` (which lives
 * inline in `checkForMention(_:)`/`insertMention(_:)`) and the Android
 * implementation in
 * `app/src/main/java/com/zoey/zoeyapp/ui/components/MentionPicker.kt`
 * (`extractMentionQuery` + `applyMention`).
 */

/**
 * Information about an active `@mention` token at the cursor.
 *
 * - `start` — index of the `@` character in the source text.
 * - `query` — characters typed after `@` and before the cursor (may be empty
 *   when the user has just typed `@`).
 */
export interface MentionAnchor {
  start: number;
  query: string;
}

/**
 * Returns the active mention anchor when the cursor is currently inside an
 * `@mention` token, or `null` otherwise.
 *
 * A mention is "active" iff:
 * - There is an `@` at or before the cursor.
 * - The `@` is at the start of the text, or immediately preceded by
 *   whitespace (space, tab, newline). `foo@bar` does NOT trigger — that's
 *   an inline word continuation (e.g. an email address).
 * - The substring between the `@` and the cursor contains no whitespace
 *   (a space or newline closes the mention).
 *
 * Edge cases:
 * - Cursor index outside `[0, text.length]` is clamped.
 * - Returns `null` when there is no `@` before the cursor.
 */
export function findActiveMention(
  text: string,
  cursorIndex: number,
): MentionAnchor | null {
  const cursor = Math.max(0, Math.min(cursorIndex, text.length));
  const before = text.slice(0, cursor);
  const lastAt = before.lastIndexOf("@");
  if (lastAt === -1) return null;

  // `@` must be at the start of the text or follow whitespace, so we don't
  // trigger on `foo@bar` (inline word continuation, e.g. email addresses).
  if (lastAt > 0) {
    const prevChar = text.charAt(lastAt - 1);
    if (!/\s/.test(prevChar)) return null;
  }

  const query = before.slice(lastAt + 1);
  // A space or newline after `@` means the mention has already been closed.
  if (/\s/.test(query)) return null;

  return { start: lastAt, query };
}

/**
 * Replaces the active mention anchor with `@<displayName>` (plus a trailing
 * space), preserving the prefix before `@` and the suffix after the cursor.
 *
 * The display name is derived from `member.nickname` (preferred) or
 * `member.name` (fallback). The first letter is capitalized so the inserted
 * token reads cleanly even when the user typed lowercase. If neither field
 * is set, falls back to `"Member"` to mirror the native clients.
 *
 * Returns the new text plus the cursor index that should be applied to the
 * input element so the caret lands immediately after the inserted space.
 */
export function applyMention(
  text: string,
  cursorIndex: number,
  member: { nickname?: string | null; name?: string | null },
  anchor: MentionAnchor,
): { newText: string; newCursorIndex: number } {
  const cursor = Math.max(0, Math.min(cursorIndex, text.length));
  const displayName = pickDisplayName(member);
  const replacement = `@${displayName} `;
  const prefix = text.slice(0, anchor.start);
  const suffix = text.slice(cursor);
  const newText = prefix + replacement + suffix;
  const newCursorIndex = anchor.start + replacement.length;
  return { newText, newCursorIndex };
}

/**
 * Capitalizes the first character of `value`. Returns `value` unchanged when
 * already empty.
 */
function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function pickDisplayName(member: {
  nickname?: string | null;
  name?: string | null;
}): string {
  const nickname = member.nickname?.trim();
  if (nickname) return capitalize(nickname);
  const name = member.name?.trim();
  if (name) return capitalize(name);
  return "Member";
}

/**
 * Scan a message body for `@nickname` / `@name` tokens and resolve them to
 * `member_profiles/{uid}` IDs. Mirrors iOS
 * `MentionRenderer.resolveMentions(in:memberMap:)` (referenced from
 * `AddEditPostView.swift:241`). Used to populate `posts.mentions: [uid]`
 * on create + edit so the backend `post_mention` push fires.
 *
 * Matching is case-insensitive against `nickname` (preferred) and `name`.
 * Duplicate mentions of the same member resolve to one uid in the
 * returned array. Members without `uid` are skipped.
 */
export function resolveMentions(
  text: string,
  members: Array<{ uid?: string | null; nickname?: string | null; name?: string | null }>,
): string[] {
  if (!text) return [];

  // Build lowercase-key → uid map. Nickname takes priority over name.
  const nameToUid = new Map<string, string>();
  for (const m of members) {
    if (!m.uid) continue;
    const nickname = m.nickname?.trim().toLowerCase();
    const name = m.name?.trim().toLowerCase();
    if (nickname && !nameToUid.has(nickname)) nameToUid.set(nickname, m.uid);
    if (name && !nameToUid.has(name)) nameToUid.set(name, m.uid);
  }

  const seen = new Set<string>();
  const result: string[] = [];

  // Match `@<token>` where token is a sequence of word chars (letters,
  // digits, underscore). iOS uses the same `@(\w+)` pattern in
  // MentionRenderer.
  const regex = /@(\w+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const token = match[1].toLowerCase();
    const uid = nameToUid.get(token);
    if (uid && !seen.has(uid)) {
      seen.add(uid);
      result.push(uid);
    }
  }
  return result;
}

/**
 * Tokenize a body of text into an ordered list of plain-text segments
 * and resolved/unresolved mention tokens, ready for inline rendering
 * by `<MentionText>`. Pure so it's testable without a DOM.
 *
 * Resolved tokens (e.g. `@Alice` when Alice is in the household) carry
 * the matched member's uid so consumers can navigate to a profile if
 * desired. Unresolved tokens (e.g. `@stranger`) are returned as plain
 * text, mirroring iOS MentionRenderer behavior.
 */
export type MentionPart =
  | { kind: "text"; value: string }
  | { kind: "mention"; token: string; uid: string | null };

export function tokenizeMentions(
  text: string,
  members: Array<{ uid?: string | null; nickname?: string | null; name?: string | null }>,
): MentionPart[] {
  if (!text) return [];

  const lookup = new Map<string, string>();
  for (const m of members) {
    if (!m.uid) continue;
    const nickname = m.nickname?.trim().toLowerCase();
    const name = m.name?.trim().toLowerCase();
    if (nickname && !lookup.has(nickname)) lookup.set(nickname, m.uid);
    if (name && !lookup.has(name)) lookup.set(name, m.uid);
  }

  const parts: MentionPart[] = [];
  const regex = /@(\w+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    }
    const uid = lookup.get(match[1].toLowerCase()) ?? null;
    parts.push({ kind: "mention", token: match[0], uid });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return parts;
}
